import React, { useCallback, useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import { TertiaryBody } from '@grnhse/seedling/lib/birch/typography';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useQueryClient } from '@tanstack/react-query';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { createBulkRediscoveryActions } from 'talent_matching/api';
import type { RediscoveryApplication } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.bulk_actions.add_tags');

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  applicationIds: number[];
  totalSelected: number;
};

const AddTagsModal = ({
  isOpen,
  closeModal,
  applicationIds,
  totalSelected,
}: Props) => {
  const {
    paths: { getTagsPath },
    hiringPlanId,
  } = useUserContext();
  const {
    paths: { bulkAddTagsPath },
  } = useRediscoveryTabContext();

  const [selectedTags, setSelectedTags] = useState<Item[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const fetchItems = useCallback(
    async (term: string) => {
      const queryKey = ['tags', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }
      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(
            getTagsPath,
            { params: { term } }
          );
          return response.data.results;
        },
        staleTime: 1000 * 60 * 5,
      });
      return { items };
    },
    [getTagsPath, queryClient]
  );

  const dropdownProps = useMultiDropdown({
    fetchItems,
    paginated: false,
    selectedItems: selectedTags,
    onSelectedItemsChange: (changes) => setSelectedTags(changes.selectedItems),
    creatable: true,
    preventLoadOnMount: false,
  });

  const onSubmit = async () => {
    setSubmitting(true);

    const formData = new FormData();
    applicationIds.forEach((id) => {
      formData.append('application_ids[]', id.toString());
    });
    selectedTags.forEach((tag, index) => {
      formData.append(`tags[${index}][id]`, tag.value.toString());
      formData.append(`tags[${index}][name]`, tag.label);
    });

    try {
      const res = await standardRequestWithErrorFlash.post(
        bulkAddTagsPath ?? '',
        formData,
        { successMessage: t('success', { count: totalSelected }) }
      );
      if (res.data.status === 'success') {
        applicationIds.forEach((id) => {
          const cached = queryClient.getQueryData<RediscoveryApplication>([
            'rediscovery-application',
            id,
          ]);
          if (!cached) {
            return;
          }
          const seen = new Set(cached.tags.map((tag) => tag.value.toString()));
          queryClient.setQueryData(['rediscovery-application', id], {
            ...cached,
            tags: [
              ...cached.tags,
              ...selectedTags.filter((tag) => !seen.has(tag.value.toString())),
            ],
          });
        });
        createBulkRediscoveryActions({
          action_type: 'tags_amended',
          application_ids: applicationIds,
          hiring_plan_id: hiringPlanId,
          metadata: { tags: selectedTags.map((tag) => tag.value) },
        });
        closeModal();
      } else {
        setSubmitting(false);
      }
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title')}
      size="medium"
    >
      <DataEntryModal.Header onClick={closeModal}>
        {t('title', { count: totalSelected })}
      </DataEntryModal.Header>

      <DataEntryModal.Body>
        <FullWidth>
          <div className="pii-blur">
            <MultiDropdown dropdownProps={dropdownProps}>
              <MultiDropdown.Field
                label={t('label')}
                placeholder={t('placeholder')}
              />
              <MultiDropdown.Flyout />
            </MultiDropdown>
          </div>
          <Stack
            sx={{
              marginLeft: Tokens.spacing['2'],
              marginTop: Tokens.spacing['0.5'],
              marginRight: Tokens.spacing['2'],
            }}
          >
            <TertiaryBody>{t('note')}</TertiaryBody>
          </Stack>
        </FullWidth>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack
          direction="row"
          spacing={Tokens.spacing['2']}
          justifyContent="flex-end"
        >
          <SecondaryButton onClick={closeModal} disabled={submitting}>
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton
            onClick={onSubmit}
            disabled={submitting || selectedTags.length === 0}
          >
            {t('confirm')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default AddTagsModal;
